// «Разметка страницы» (как в Word): текст на листах A4 с полями по СТО, построчный перенос между страницами, номера
// страниц, сноски внизу листа, оглавление с номерами страниц, «Страница N из M», навигатор страниц.
// Модель документа и экспорт не меняются - только отображение (распорки-виджеты + слой листов).
import type { RegistryApi } from '../../core/registry';
import { appStore, readPref, writePref } from '../../core/store';
import { runCommand } from '../../core/ctx';
import { uiStore } from '../../ui/uiState';
import { Icon } from '../../ui/icons';
import { PagesExtension } from './plugin';
import { NbspExtension } from './nbsp';
import { TocPages } from './PagesLayer';
import { tocOn } from '../sto_intel/data';
import { PageNavigator, gotoPage } from './navigator';
import { usePages, pagesStore } from './store';
import { askText } from '../../ui/prompts';
import './pages.css';

function PageStatus() {
  const ready = usePages((s) => s.ready);
  const cur = usePages((s) => s.current);
  const front = usePages((s) => s.frontCount);
  const main = usePages((s) => s.main.length);
  if (!ready || !main) return null;
  const total = front + main;
  return (
    <button type="button" className="status-item status-btn" data-testid="status-page" onClick={() => runCommand('view.pageNav')}
      data-tip="Страница курсора и число страниц - по разметке страниц (как в Word)" data-tip-desc="Щелчок - навигатор страниц">
      <Icon name="file" size={13} /> Страница {Math.min(Math.max(cur, 1), total)} из {total}
    </button>
  );
}

export default function setup(r: RegistryApi) {
  r.extensions.add({ id: 'pages.nbsp', order: 89, create: () => NbspExtension });
  r.extensions.add({ id: 'pages.layout', order: 90, create: () => PagesExtension });
  // оглавление - листами, с номерами страниц из разметки (заменяет лист оглавления sto_intel); выключенное
  // (meta.toc_page, чистый лист) - без рамки листа: иначе пустая рамка считалась страницей и сдвигала номера
  r.sheets.add({ id: 'toc', order: 50, component: TocPages, visible: (c) => tocOn(c.store.getState().meta) });
  r.statusbar.add({ id: 'page', side: 'left', order: 5, component: PageStatus });
  r.panels.add({ id: 'pagenav', title: 'Навигатор', icon: 'file', side: 'left', order: 15, component: PageNavigator });
  r.commands.add({
    id: 'view.pages', title: 'Разметка страницы', icon: 'pagebreak',
    description: 'Текст на листах A4, как в Word: поля, переносы между страницами, номера страниц',
    run: () => uiStore.getState().set({ showPages: !uiStore.getState().showPages }), isActive: () => !!uiStore.getState().showPages,
  });
  r.ribbonItems.add({ id: 'show-pages', group: 'view.show', type: 'toggle', command: 'view.pages', label: 'Разметка страницы' });
  // «Как при печати»: лист без служебного (подсказки, волнистые подчёркивания проверки, рамки выделения, заполнители,
  // номера строк кода) - видно ровно то, что будет в DOCX/PDF
  const setPrint = (on: boolean) => {
    document.documentElement.classList.toggle('pg-print', on);
    writePref('pageview.print', on ? '1' : '0');
  };
  setPrint(readPref('pageview.print', '0') === '1');
  r.commands.add({
    id: 'view.printLook', title: 'Как при печати', icon: 'print',
    description: 'Показать листы без служебных пометок - как будет напечатано (DOCX/PDF)',
    run: () => { setPrint(!document.documentElement.classList.contains('pg-print')); uiStore.getState().set({}); },
    isActive: () => document.documentElement.classList.contains('pg-print'),
  });
  r.ribbonItems.add({ id: 'show-print', group: 'view.show', type: 'toggle', command: 'view.printLook', label: 'Как при печати' });
  r.commands.add({
    id: 'view.pageNav', title: 'Навигатор страниц', icon: 'file', description: 'Миниатюры страниц: щелчок - перейти',
    run: () => { const p = appStore.getState().panels; appStore.getState().set({ panels: { ...p, left: p.left === 'pagenav' ? null : 'pagenav' } }); },
  });
  r.commands.add({
    id: 'view.gotoPage', title: 'Перейти к странице…', icon: 'file', shortcut: 'Mod-g',
    run: async (ctx) => {
      const v = await askText({ title: 'Перейти к странице', label: 'Номер страницы', value: String(pagesStore.getState().current || 1) });
      const n = Number(String(v || '').trim());
      if (n > 0) gotoPage(ctx, n);
    },
  });
  // режим «Разметка страницы» - по умолчанию (раньше был выключаемый слой «Границы страниц»)
  if (readPref('pageview.v2', '') !== '1') {
    writePref('pageview.v2', '1');
    uiStore.getState().set({ showPages: true });
  }
}
